import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// APP ICONS
import DateFilter from "./dateFilter";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// ICONS
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const buttonVariants = {
  hover: { scale: 1.05, boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)" },
  tap: { scale: 0.98 },
};

const chipVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (custom) => ({
    opacity: 1,
    scale: 1,
    marginRight: 2,
    transition: {
      delay: custom * 0.05,
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  }),
};

export default function SearchFilter({
  title,
  filters = [],
  filterDate = false,
  shouldCenter = true,
  submit,
  initialExpanded = false,
  showHeader = true,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  const [selectedFilters, setSelectedFilters] = useState({});
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [contentHeight, setContentHeight] = useState("auto");
  const contentRef = React.useRef(null);

  // Map to store filter display names
  const [filterDisplayNames, setFilterDisplayNames] = useState({});

  // To prevent state loss during transitions, we'll keep track of animations
  const [isAnimating, setIsAnimating] = useState(false);

  // Collect filter component display names on mount
  useEffect(() => {
    const displayNames = {};

    if (filterDate) {
      displayNames["DateFilter"] = "Período";
    }

    filters.forEach((FilterComponent) => {
      if (FilterComponent.displayName) {
        displayNames[FilterComponent.name] = FilterComponent.displayName;
      } else {
        // Try to create a readable name from the component name
        const name = FilterComponent.name
          ?.replace(/([A-Z])/g, " $1") // Add space before capital letters
          ?.replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
          ?.replace(/Filter$/, "") // Remove "Filter" suffix
          ?.trim();

        displayNames[FilterComponent.name] = name || FilterComponent.name;
      }
    });

    setFilterDisplayNames(displayNames);
  }, [filters, filterDate]);

  const handleFilterChange = (filterName, value) => {
    setSelectedFilters((prevFilters) => {
      // Make a deep copy to avoid any reference issues
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const handleSubmit = () => {
    submit(selectedFilters);
  };

  const toggleExpanded = () => {
    if (!isAnimating) {
      setIsExpanded((prev) => !prev);
    }
  };

  const clearFilter = (filterName) => {
    setSelectedFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters[filterName];
      return newFilters;
    });
  };

  // Measure content height for animations
  useEffect(() => {
    if (contentRef.current && isExpanded) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, selectedFilters]);

  // Count active filters
  useEffect(() => {
    let count = 0;

    Object.entries(selectedFilters).forEach(([key, value]) => {
      // Check if the filter has a meaningful value
      if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "object") {
          // Check if it's an object with value property (like autocomplete selections)
          if (value.value && value.value !== "ALL") {
            count++;
          }
          // For date filters that have dateStartString and dateEndString
          else if (value.dateStartString && value.dateEndString) {
            count++;
          }
        } else if (typeof value === "string" && value.trim() !== "") {
          count++;
        }
      }
    });

    setActiveFiltersCount(count);
  }, [selectedFilters]);

  useEffect(() => {
    // Wait until after initial render to avoid unnecessary search on component mount
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
  }, [isInitialRender]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && !isAnimating) {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [selectedFilters, isAnimating]);

  const animationStartHandler = () => {
    setIsAnimating(true);
  };

  const animationCompleteHandler = () => {
    setIsAnimating(false);
  };

  // Format filter value for display
  const formatFilterValue = (filterName, value) => {
    if (!value) return "";

    // Handle date filter
    if (filterName === "DateFilter") {
      if (value.dateStartString && value.dateEndString) {
        // Convert the ISO strings to Date objects
        const startDate = new Date(value.dateStartString);
        const endDate = new Date(value.dateEndString);

        // Format the dates
        const formatDate = (date) => {
          return new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "2-digit",
          }).format(date);
        };

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
      }
      return "";
    }

    // Handle object with label/value (like autocomplete)
    if (value.label) {
      return value.label;
    }

    // Handle string values
    if (typeof value === "string") {
      return value;
    }

    return JSON.stringify(value);
  };

  // Render active filters as chips
  const renderActiveFilters = () => {
    if (isExpanded || activeFiltersCount === 0) return null;

    return (
      <Box
        sx={{
          mt: 2,
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              display: "flex",
              opacity: 1,
              transition: { staggerChildren: 0.05 },
            },
          }}
        >
          {Object.entries(selectedFilters).map(([filterName, value], index) => {
            // Skip null or empty values
            if (!value || (typeof value === "string" && !value.trim()))
              return null;

            // Skip objects with no meaningful value
            if (
              typeof value === "object" &&
              (value.value === "ALL" ||
                (!value.value && !value.dateStartString))
            ) {
              return null;
            }

            const displayName = filterDisplayNames[filterName] || filterName;
            const displayValue = formatFilterValue(filterName, value);

            // Skip if we couldn't format a display value
            if (!displayValue) return null;

            // Generate appropriate icon based on filter type
            let icon = <TuneIcon sx={{ fontSize: isMobile ? 16 : 18 }} />;
            if (filterName === "DateFilter") {
              icon = (
                <CalendarTodayIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
              );
            }

            return (
              <motion.div
                key={filterName}
                variants={chipVariants}
                custom={index}
              >
                <Chip
                  icon={icon}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        "& .divider": {
                          display: "inline-block",
                          width: "1px",
                          height: "12px",
                          backgroundColor: alpha(
                            theme.palette.primary.contrastText,
                            0.5
                          ),
                          mx: 0.5,
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          opacity: 0.9,
                          fontSize: isMobile ? "0.65rem" : "0.75rem",
                        }}
                      >
                        {displayName}:
                      </Typography>
                      <span className="divider" />
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontWeight: 400,
                          fontSize: isMobile ? "0.65rem" : "0.75rem",
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Box>
                  }
                  deleteIcon={<ClearIcon fontSize="small" />}
                  onDelete={() => clearFilter(filterName)}
                  color="primary"
                  sx={{
                    borderRadius: 1.5,
                    height: isMobile ? 28 : 32,
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </Box>
    );
  };

  // Calculate dynamic padding based on screen size
  const containerPadding = isMobile ? 2 : isTablet ? 2.5 : 0;

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      onAnimationStart={animationStartHandler}
      onAnimationComplete={animationCompleteHandler}
      sx={{
        p: containerPadding,
        position: "relative", // Important for containing absolute elements
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        mx: "auto", // Center on all devices
      }}
    >
      {/* Header with title and expand/collapse button - Mobile optimized */}
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            mb: isExpanded ? (isMobile ? 2 : 3) : 1,
            gap: isMobile ? 2 : 0,
          }}
        >
          {title && title.length > 0 ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 1.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.light, 0.1),
                width: isMobile ? "100%" : "auto",
                boxSizing: "border-box",
              }}
            >
              <FilterAltIcon
                sx={{
                  mr: 1.5,
                  color: theme.palette.primary.main,
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                }}
              />
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                fontWeight={600}
                color="primary"
              >
                {title}
              </Typography>
            </Box>
          ) : (
            <Box />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "space-between" : "flex-end",
            }}
          >
            {!isExpanded && activeFiltersCount > 0 && (
              <Chip
                icon={<TuneIcon />}
                label={`${activeFiltersCount} ${
                  activeFiltersCount === 1 ? "filtro ativo" : "filtros ativos"
                }`}
                color="primary"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  fontWeight: 500,
                  height: isMobile ? 28 : 32,
                  "& .MuiChip-label": {
                    px: isMobile ? 1 : 1.5,
                  },
                }}
              />
            )}

            <Tooltip
              title={isExpanded ? "Recolher filtros" : "Expandir filtros"}
            >
              <IconButton
                onClick={toggleExpanded}
                size={isMobile ? "small" : "medium"}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  p: isMobile ? 1 : 1.5,
                }}
              >
                {isExpanded ? (
                  <ExpandLessIcon fontSize={isMobile ? "small" : "medium"} />
                ) : (
                  <ExpandMoreIcon fontSize={isMobile ? "small" : "medium"} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Render active filter chips */}
      {renderActiveFilters()}

      {/* Divider shown only when expanded */}
      <AnimatePresence>
        {isExpanded && showHeader && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Divider sx={{ my: isMobile ? 1 : 2, opacity: 0.6 }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter content wrapper with dynamic height animation */}
      <Box
        sx={{
          // Always render content, but control visibility with CSS
          display: isExpanded ? "block" : "none",
          mb: isExpanded ? (isMobile ? 2 : 3) : 0,
          position: "relative",
          // When expanded, make sure overflow is visible for dropdowns
          overflow: isExpanded ? "visible" : "hidden",
        }}
      >
        {/* Actual filter content - maintains layout and state */}
        <Box ref={contentRef}>
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            {...(shouldCenter
              ? {
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }
              : {})}
          >
            {/* Date Filter */}
            {filterDate && (
              <Grid item xs={12}>
                <DateFilter
                  key="date-filter"
                  selectedValue={selectedFilters[DateFilter.name]}
                  onFilterChange={(value) => {
                    handleFilterChange(DateFilter.name, value);
                  }}
                />
                <Divider sx={{ my: 2 }} />
              </Grid>
            )}

            {/* Other Filters - Responsive grid layout */}
            {filters.map((FilterComponent, index) => (
              <Grid
                item
                xs={12}
                sm={isTablet ? 6 : 12}
                md={4}
                lg={isLargeScreen ? 3 : 4}
                key={`grid-${index}`}
              >
                <FilterComponent
                  key={`filter-${FilterComponent.name || index}`}
                  selectedValue={selectedFilters[FilterComponent.name]}
                  onFilterChange={(value) =>
                    handleFilterChange(FilterComponent.name, value)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Search button - always visible, mobile-optimized */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          mt: isExpanded ? 0 : isMobile ? 2 : 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <motion.div
          whileHover="hover"
          whileTap="tap"
          variants={buttonVariants}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "large" : "large"}
            startIcon={<SearchIcon />}
            onClick={handleSubmit}
            sx={{
              px: isMobile ? 3 : 4,
              py: isMobile ? 1.5 : 1.2,
              borderRadius: 2,
              minWidth: isMobile ? "100%" : "200px",
              fontWeight: 600,
              fontSize: isMobile ? "0.9rem" : "1rem",
              textTransform: "none",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 16px ${alpha(
                  theme.palette.primary.main,
                  0.4
                )}`,
              },
              // Larger touch target on mobile
              height: isMobile ? "48px" : "auto",
            }}
          >
            Pesquisar
          </Button>
        </motion.div>
      </Box>
    </Box>
  );
}
